import { DataService, SubscribeForNotificationsType } from '../data/src';
import { ConvertHelper, Environment } from '../helpers';
import { PushAdapterErrorType, PushAdapterSettings } from '../models/notifications.model';

export interface PushAdapter {
  activatePushNotifications(): Promise<void>;
  deactivatePushNotifications(): Promise<void>;
  checkActive(): Promise<void>;
}

export abstract class PushAdapterBase<TSubscription> implements PushAdapter {
  private isActive: boolean;
  private pushSubscriptionIdentifier: TSubscription;
  protected existsMasterDBSubscription: boolean;
  public isActivatable: boolean;
  constructor(
    private notificationsType: SubscribeForNotificationsType,
    private coreDataService: DataService,
    protected channelIdentifier: string,
    public settings: PushAdapterSettings,
    private clientIdentifier: string
  ) {
    if (!this.clientIdentifier) {
      this.clientIdentifier = Environment.getClientId();
    }
  }

  public async checkActive(): Promise<void> {
    const result = await this.coreDataService
      .checkSubscriptionForNotifications({
        Type: this.notificationsType,
        ClientIdentifier: this.clientIdentifier,
        ChannelIdentifier: this.channelIdentifier,
      })
      .toPromise();
    if (ConvertHelper.toBoolean(result)) {
      this.existsMasterDBSubscription = true;
      await this.activatePushNotifications();
    } else if (this.hasPushSubscriptionIdentifier) {
      await this.deactivatePushNotifications();
    }
  }

  private enabling: Promise<void> = null;
  public async activatePushNotifications(): Promise<void> {
    if (!this.isActive) {
      if (!this.enabling) {
        this.enabling = this.enablePushNotificationsCore();
      }
      try {
        await this.enabling;
      } finally {
        this.enabling = null;
      }
    }
  }

  protected async requestPermission(): Promise<void> {
    await Promise.race([
      Notification.requestPermission(),
      new Promise<void>((_, reject) =>
        setTimeout(() => reject(PushAdapterErrorType.TimeoutWhileRequestingPermissions), 8000)
      ),
    ]);
  }

  private disabling: Promise<void> = null;
  public async deactivatePushNotifications(): Promise<void> {
    if (!this.disabling) {
      this.disabling = this.disablePushNotificationsCore();
    }
    try {
      await this.disabling;
    } finally {
      this.disabling = null;
    }
  }

  private async disablePushNotificationsCore(): Promise<void> {
    try {
      if (this.isActive) {
        await this.coreDataService
          .unsubscribeForNotifications({
            Type: this.notificationsType,
            ClientIdentifier: this.clientIdentifier,
            ChannelIdentifier: this.channelIdentifier,
          })
          .toPromise();
        this.existsMasterDBSubscription = false;
        this.unsubscribe();
        this.notifyStateChanged(false, true);
      }
    } catch (err) {
      this.settings.onError(PushAdapterErrorType.UnableToUnSubscribeOnWebApiEndpoint, err);
      if (this.hasPushSubscriptionIdentifier) {
        this.active(true);
      }
    }
  }

  protected async notifyPushSubscription(subscription: TSubscription): Promise<void> {
    try {
      if (!subscription) {
        this.notifyStateChanged(false, true);
      } else {
        this.pushSubscriptionIdentifier = subscription;
        this.notifyStateChanged(true, true);

        await this.coreDataService
          .subscribeForNotifications({
            Type: this.notificationsType,
            ClientIdentifier: this.clientIdentifier,
            ChannelIdentifier: this.channelIdentifier,
            ContextDefinition: this.settings.getContextDefinition
              ? await this.settings.getContextDefinition()
              : undefined,
            ProviderDefinition: await this.getProviderDefinition(subscription),
          })
          .toPromise();
      }
    } catch (err) {
      this.settings.onError(PushAdapterErrorType.UnableToSubscribeOnWebApiEndpoint, err);
      this.unsubscribeCore(subscription);
      this.active(false);
    }
  }

  protected notifyStateChanged(active: boolean, activatable: boolean): void {
    this.active(active);
    this.activatable(activatable);
  }

  protected active(value: boolean) {
    this.isActive = value;
    if (this.settings.onActiveChanged) {
      this.settings.onActiveChanged(value);
    }
  }

  protected activatable(value: boolean) {
    this.isActivatable = value;
    if (this.settings.onActivatableChanged) {
      this.settings.onActivatableChanged(value);
    }
  }

  protected get hasPushSubscriptionIdentifier(): boolean {
    return !!this.pushSubscriptionIdentifier;
  }

  protected async unsubscribe(): Promise<void> {
    if (this.pushSubscriptionIdentifier) {
      await this.unsubscribeCore(this.pushSubscriptionIdentifier);
      this.pushSubscriptionIdentifier = null;
    }
  }

  protected abstract isGranted(): Promise<boolean>;
  protected abstract isDenied(): Promise<boolean>;
  protected abstract unsubscribeCore(subscription: TSubscription): Promise<void>;
  protected abstract getProviderDefinition(subscription: TSubscription): Promise<object>;
  protected abstract enablePushNotificationsCore(): Promise<void>;
}
